import type { IconName } from "@/components/ui/icon";

// TODO(a11y AAA-contrast): The `gradientColor` hex values below pair with low-saturation gradient
// backgrounds and likely fail WCAG SC 1.4.6 (Enhanced Contrast, 7:1) for the icon glyphs and
// accompanying text usages elsewhere in the feature. Achieving AAA here requires recalibrating
// these brand swatches together with the gradients in this file. Tracked outside of this
// feature-scoped pass.

/** Preset slugs that the create/edit form supports. `arbitrator-criteria` is excluded
 * because it has no entity-input UI yet. */
export const PRESET_TYPE_SLUGS = [
  "companies",
  "universities",
  "job-titles",
  "skills",
  "investors",
  "locations",
  "filters",
  "job-stages",
] as const;

export type PresetTypeSlug = (typeof PRESET_TYPE_SLUGS)[number];

export interface PresetCategoryDef {
  id: string;
  slug: string;
  apiType: string;
  label: string;
  description: string;
  icon: IconName;
  color: string;
  emptyTitle: string;
  emptyDescription: string;
  searchLabel?: string;
  searchPlaceholder?: string;
  entityNoun?: string;
  autocompleteField?: string;
  allowFreeText?: boolean;
}

export const PRESET_CATEGORIES: PresetCategoryDef[] = [
  {
    id: "companies",
    slug: "companies",
    apiType: "companies",
    label: "Companies",
    description: "Quickly add predefined companies to your talent search.",
    icon: "IconBuildings",
    color: "#ff9258",
    emptyTitle: "No presets available",
    emptyDescription: "No company presets yet! Create one to get started.",
    searchLabel: "Search for companies to add",
    searchPlaceholder: "Enter company names",
    entityNoun: "companies",
    autocompleteField: "companies",
    allowFreeText: false,
  },
  {
    id: "universities",
    slug: "universities",
    apiType: "universities",
    label: "Educational Institutions",
    description: "Apply preset schools and universities to filter talent.",
    icon: "IconSchool",
   color: "#58ff9e",
    emptyTitle: "No presets available",
    emptyDescription: "No university presets yet! Create one to get started.",
    searchLabel: "Search for universities to add",
    searchPlaceholder: "Enter university names",
    entityNoun: "universities",
    autocompleteField: "universities",
    allowFreeText: true,
  },
  {
    id: "job-titles",
    slug: "job-titles",
    apiType: "job_titles",
    label: "Job Titles",
    description: "Instantly include common job titles in your search.",
    icon: "IconBag",
   color:"#ffe658",
    emptyTitle: "No presets available",
    emptyDescription: "No job title presets yet! Create one to get started.",
    searchLabel: "Search for job titles to add",
    searchPlaceholder: "Enter job titles",
    entityNoun: "job titles",
    autocompleteField: "job_titles",
    allowFreeText: true,
  },
  {
    id: "arbitrator-criteria",
    slug: "arbitrator-criteria",
    apiType: "arbitrator_criteria",
    label: "Arbitrator Criteria",
    description: "Quickly add predefined criteria to arbitrator sessions.",
    icon: "IconListSparkle",
    color: "#58a6ff",
    emptyTitle: "No presets available",
    emptyDescription:
      "No arbitrator criteria presets yet! Create one to get started.",
  },
  {
    id: "investors",
    slug: "investors",
    apiType: "investors",
    label: "Investors",
    description: "Add known investors or firms as search entities.",
    icon: "IconDollar",
    color: "#ca58ff",
    emptyTitle: "No presets available",
    emptyDescription: "No investor presets yet! Create one to get started.",
    searchLabel: "Search for investors to add",
    searchPlaceholder: "Enter investor names",
    entityNoun: "investors",
    autocompleteField: "companies",
    allowFreeText: true,
  },
  {
    id: "skills",
    slug: "skills",
    apiType: "skills",
    label: "Skills",
    description: "Apply preset skill sets to refine talent results.",
    icon: "IconWhiteboard",
    color: "#ff588d",
    emptyTitle: "No presets available",
    emptyDescription: "No skill presets yet! Create one to get started.",
    searchLabel: "Search for skills to add",
    searchPlaceholder: "Enter skill names",
    entityNoun: "skills",
    autocompleteField: "skills",
    allowFreeText: true,
  },
  {
    id: "locations",
    slug: "locations",
    apiType: "locations",
    label: "Locations",
    description: "Add preset location groups for quick search setup.",
    icon: "IconMapPin",
    color: "#58ffd6",
    emptyTitle: "No presets available",
    emptyDescription: "No location presets yet! Create one to get started.",
    searchLabel: "Search for locations to add",
    searchPlaceholder: "Enter location names",
    entityNoun: "locations",
    autocompleteField: "locations",
    allowFreeText: true,
  },
  {
    id: "filters",
    slug: "filters",
    apiType: "filters",
    label: "Filters",
    description: "Save and reuse complete filter configurations.",
    icon: "IconFilterTimeline",
    color: "#58d6ff",
    emptyTitle: "No presets available",
    emptyDescription: "No filter presets yet! Create one to get started.",
    searchLabel: "Configure filters",
    searchPlaceholder: "Search filters",
    entityNoun: "filters",
    autocompleteField: "",
    allowFreeText: false,
  },
  {
    id: "job-stages",
    slug: "job-stages",
    apiType: "job_stages",
    label: "Job Stages",
    description: "Save and reuse pipeline stage setups across jobs.",
    icon: "IconPlan",
    color: "#9f70ff",
    emptyTitle: "No presets available",
    emptyDescription: "No job stage presets yet! Create one to get started.",
    searchLabel: "Configure stages",
    searchPlaceholder: "Search stage presets",
    entityNoun: "stages",
    autocompleteField: "",
    allowFreeText: false,
  },
];

export function getPresetCategory(
  slug: string | undefined,
): PresetCategoryDef | undefined {
  if (!slug) return undefined;
  return PRESET_CATEGORIES.find((c) => c.slug === slug);
}

/** Resolved create-flow metadata for a form-supported preset slug. All fields are
 * non-optional here so the form never has to worry about missing values. */
export interface PresetTypeMeta {
  label: string;
  icon: IconName;
  searchLabel: string;
  searchPlaceholder: string;
  entityNoun: string;
  apiType: string;
  autocompleteField: string;
  allowFreeText: boolean;
}

export function getPresetMeta(slug: PresetTypeSlug): PresetTypeMeta {
  const cat = PRESET_CATEGORIES.find((c) => c.slug === slug);
  return {
    label: cat?.label ?? slug,
    icon: cat?.icon ?? "IconBuildings",
    apiType: cat?.apiType ?? slug,
    searchLabel: cat?.searchLabel ?? `Search for ${slug} to add`,
    searchPlaceholder: cat?.searchPlaceholder ?? `Enter ${slug}`,
    entityNoun: cat?.entityNoun ?? slug,
    autocompleteField: cat?.autocompleteField ?? "",
    allowFreeText: cat?.allowFreeText ?? false,
  };
}

/** Form-supported categories, in the order presented in the create-type picker. */
export const PRESET_TYPE_CATEGORIES: PresetCategoryDef[] = PRESET_TYPE_SLUGS.map(
  (slug) => {
    const cat = PRESET_CATEGORIES.find((c) => c.slug === slug);
    if (!cat) {
      throw new Error(`Missing PRESET_CATEGORIES entry for slug "${slug}"`);
    }
    return cat;
  },
);
